import {useMutation, useQueryClient} from "@tanstack/react-query";
import {proposalApi} from "../services/proposalService";
import {proposalStatus} from "../utils/constants";
import {useNavigate} from "react-router-dom";
import {showToastModal} from "../jsx/components/shared/ToastModalSwal";

export const useProposal = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {mutate: addProposal} = useMutation({
    mutationFn: proposalApi.add,
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur s'est produite lors de l’envoi de la proposition.",
        buttonTitle: "Ressayer",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
      showToastModal({
        success: true,
        title: "Proposition envoyée",
        message: "Votre proposition a été envoyée au client avec succès.",
        buttonTitle: "Fermer",
      });
    },
  });

  const {mutate: updateProposal} = useMutation({
    mutationFn: proposalApi.update,
  });
  const {mutateAsync: payProposal} = useMutation({
    mutationFn: proposalApi.pay,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
      queryClient.invalidateQueries({queryKey: ["offers"]});
    },
  });
  const {mutate: deleteProposal} = useMutation({
    mutationFn: proposalApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
    },
  });

  const onUpdateProposal = ({
    id,
    initialBudget,
    appointmentDate,
    title,
    comment,
  }) => {
    updateProposal(
      {
        id,
        body: {initialBudget, appointmentDate, title, comment},
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["chatRooms"]});
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showToastModal({
            success: true,
            title: "Proposition modifiée",
            message: "Votre proposition a été modifiée avec succès.",
            buttonTitle: "Fermer",
          });
        },
      },
    );
  };
  const onDeleteProposal = (id) => {
    updateProposal(
      {
        id,
        body: {
          status: proposalStatus.CANCELLED,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["chatRooms"]});
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showToastModal({
            success: true,
            title: "Proposition annulée",
            message: "Votre  proposition a été annulée avec succès.",
            buttonTitle: "Fermer",
          });
        },
      },
    );
  };
  const handlePayProposal = async (proposalData) => {
    try {
      const response = await payProposal(proposalData);

      return response;
    } catch (error) {
      // Error handling
      throw error;
    }
  };

  const openPayment = async (proposalId) => {
    const response = await proposalApi.getPaymentReference({proposalId});
    const reference = response?.paymentReference;
    if (!reference) throw new Error("Référence de paiement introuvable.");
    navigate("/paiement/" + reference);
  };

  const showAcceptedProposalToast = ({id, onAccepted, acceptanceData}) => {
    showToastModal({
      success: true,
      title: "Proposition acceptée",
      message:
        "La proposition a bien été acceptée. Vérifiez le récapitulatif avant de procéder au paiement.",
      duration: 2000,
      thenCallback: () =>
        onAccepted?.({
          proposalId: id,
          ...acceptanceData,
        }),
    });
  };

  const onAcceptProposal = (id, {onAccepted} = {}) => {
    updateProposal(
      {
        id,
        body: {
          status: proposalStatus.ACCEPTED,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["chatRooms"]});
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showAcceptedProposalToast({id, onAccepted});
        },
      },
    );
  };

  const onAcceptWithAdress = (
    {id, address, zipCode, city},
    {onAccepted} = {},
  ) => {
    updateProposal(
      {
        id,
        body: {
          status: proposalStatus.ACCEPTED,
          address,
          zipCode,
          city,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["chatRooms"]});
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showAcceptedProposalToast({
            id,
            onAccepted,
            acceptanceData: {address, zipCode, city},
          });
        },
      },
    );
  };

  const onRefuseProposal = (id) => {
    updateProposal(
      {
        id,
        body: {
          status: proposalStatus.REFUSED,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["chatRooms"]});
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showToastModal({
            success: true,
            title: "Proposition refusée",
            message: "Vous avez refusé la proposition.",
            buttonTitle: "Fermer",
          });
        },
      },
    );
  };

  const onAddProposalNoOffer = ({
    initialBudget,
    appointmentDate,
    clientId,
    title,
    comment,
    directChatId,
  }) => {
    addProposal({
      body: {
        initialBudget,
        appointmentDate,
        clientId,
        title,
        comment,
        directChatId,
      },
    });
  };

  const onAddProposal = ({
    initialBudget,
    appointmentDate,
    offerId,
    title,
    comment,
  }) => {
    addProposal({
      body: {
        initialBudget,
        appointmentDate,
        offerId,
        title,
        comment,
      },
    });
  };
  return {
    onAddProposal,
    onUpdateProposal,
    onDeleteProposal,
    onAcceptProposal,
    onRefuseProposal,
    handlePayProposal,
    onAddProposalNoOffer,
    onAcceptWithAdress,
    openPayment,
  };
};
